'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, FolderOpen } from 'lucide-react';
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { StudioHeader } from '@/components/studio/StudioHeader';
import { Segment } from '@/types/segment.interface';
import { Project as Profile } from '@/types/project.interface';
import { Group } from '@/types/group.interface';
import { StudioSidebar } from '@/components/studio/StudioSidebar';
import { ProjectManagerModal } from '@/components/studio/ProjectManagerModal';
import { CreationHeader } from '@/components/studio/CreationHeader';
import { VideoPreviewModal } from '@/components/studio/VideoPreviewModal';
import { TimelineList } from '@/components/studio/TimelineList';
import { GenerationSettingsPanel } from '@/components/studio/GenerationSettingsPanel';
import { AutoWorkflowPanel } from '@/components/studio/AutoWorkflowPanel';
import { useModal } from '@/redux/useModal';
import Api from '@/lib/api';
import type { PluginMeta } from '@/lib/processors/base';

export default function VeoDirector() {
  const modaler = useModal();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();

  // Settings & Context State
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentGroup, setCurrentGroup] = useState<Group | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [plugins, setPlugins] = useState<PluginMeta[]>([]);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [isGroupLoading, setIsGroupLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSegmentsDirty, setIsSegmentsDirty] = useState(false);
  const [uploadingSegmentId, setUploadingSegmentId] = useState<number | null>(null);
  const [editingProject, setEditingProject] = useState<Profile | 'new' | null>(null);
  const [isProfileManagerOpen, setIsProfileManagerOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Preview State
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPlaylist, setPreviewPlaylist] = useState<{ url: string, title: string }[]>([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  const switchGroup = useCallback(async (groupId: string, silent = false) => {
    if (!silent) setIsGroupLoading(true);
    try {
      const data = await Api.groups.get(groupId);
      setCurrentGroup(data);
      setSegments(data.segments || []);
      setIsSegmentsDirty(false);
    } catch (error) {
      console.error("加载组数据失败:", error);
    } finally {
      if (!silent) setIsGroupLoading(false);
    }
  }, []);

  // Fetch plugin list once on mount
  useEffect(() => {
    fetch('/api/processors')
      .then(r => r.json())
      .then((data: PluginMeta[]) => { if (Array.isArray(data)) setPlugins(data); })
      .catch(console.error);
  }, []);

  // Initialization
  useEffect(() => {
    if (sessionStatus === "unauthenticated") {
      router.push("/login");
    } else if (sessionStatus === "authenticated") {
      const initData = async () => {
        try {
          const [resGroups, resProjects] = await Promise.all([
            Api.groups.list(),
            Api.projects.list()
          ]);
          setProfiles(Array.isArray(resProjects) ? resProjects : []);
          const groupList = Array.isArray(resGroups) ? resGroups : [];
          setGroups(groupList);
          if (groupList.length > 0 && !currentGroup) {
            await switchGroup(groupList[0].id, true);
          }
        } catch (error) {
          console.error("加载初始数据失败:", error);
        } finally {
          setIsLoading(false);
        }
      };
      initData();
    }
  }, [sessionStatus, router, switchGroup, currentGroup]);

  // SSE Status Updates
  useEffect(() => {
    if (!currentGroup) return;

    let eventSource: EventSource | null = null;
    let retryTimeout: NodeJS.Timeout | null = null;
    let fallbackInterval: NodeJS.Timeout | null = null;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_INTERVAL = 5000;
    const POLLING_INTERVAL = 5000;

    const handleStatusUpdate = async (statusData: any) => {
      try {
        if (statusData.isProcessing && statusData.groupId === currentGroup.id) {
          setIsGenerating(true);
          await switchGroup(currentGroup.id, true);
        } else if (isGenerating && !statusData.isProcessing) {
          setIsGenerating(false);
          await switchGroup(currentGroup.id, false);
          const groupsData = await Api.groups.list();
          setGroups(Array.isArray(groupsData) ? groupsData : []);

          if (statusData.error) {
            modaler.error(`生成出错: ${statusData.error}`);
          }
        }
      } catch (err) {
        console.error("Status update processing error:", err);
      }
    };

    const startPollingFallback = () => {
      console.warn("[SSE Fallback] 连续失败，切换到短轮询模式...");
      if (fallbackInterval) clearInterval(fallbackInterval);
      fallbackInterval = setInterval(async () => {
        try {
          const res = await fetch("/api/generate/status");
          if (res.ok) {
            const status = await res.json();
            handleStatusUpdate(status);
            // 如果已经停止生成，可以考虑尝试恢复 SSE
            if (!status.isProcessing && retryCount >= MAX_RETRIES) {
              retryCount = 0;
            }
          }
        } catch (e) {
          console.error("[Polling] 轮询请求失败:", e);
        }
      }, POLLING_INTERVAL);
    };

    const connectSSE = () => {
      if (eventSource) eventSource.close();
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
        fallbackInterval = null;
      }

      console.log(`[SSE] 正在建立连接... (尝试 ${retryCount + 1}/${MAX_RETRIES})`);
      eventSource = new EventSource("/api/events");

      eventSource.onmessage = (event) => {
        retryCount = 0;
        try {
          const statusData = JSON.parse(event.data);
          handleStatusUpdate(statusData);
        } catch (err) {
          // 忽略非 JSON 数据（如 :heartbeat）
        }
      };

      eventSource.onerror = (err) => {
        console.warn("[SSE] 连接中断/错误:", err);
        eventSource?.close();
        eventSource = null;

        if (retryCount < MAX_RETRIES) {
          retryCount++;
          retryTimeout = setTimeout(connectSSE, RETRY_INTERVAL);
        } else {
          startPollingFallback();
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSource) eventSource.close();
      if (retryTimeout) clearTimeout(retryTimeout);
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [currentGroup?.id, isGenerating, switchGroup, modaler]);

  const openPreview = (item: Segment | { url: string, title: string }) => {
    const url = 'url' in item ? item.url : item.videoUrl;
    const title = 'title' in item ? item.title : (item.desc || '未命名片段');
    if (!url) return;
    setPreviewPlaylist([{ url, title }]);
    setPreviewInitialIndex(0);
    setIsPreviewOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center transition-colors">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans overflow-hidden transition-colors duration-300">
      <StudioSidebar
        groups={groups}
        setGroups={setGroups}
        currentGroupId={currentGroup?.id}
        onSelect={switchGroup}
        profiles={profiles}
        onOpenSettings={() => setIsProfileManagerOpen(true)}
        onViewResult={(group) => openPreview({ url: group.finalVideoUrl!, title: `最终成品: ${group.name}` })}
      />

      <main className="flex-1 flex flex-col overflow-hidden relative bg-zinc-50 dark:bg-transparent">
        {(isGroupLoading || isLoading) && (
          <div className="absolute inset-0 bg-white/50 dark:bg-zinc-950/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <Loader2 className="animate-spin text-blue-500" size={32} />
          </div>
        )}

        <StudioHeader userName={session?.user?.name}
          onOpenSettings={() => setIsProfileManagerOpen(true)}
        />

        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar relative pb-24">
          <div className="max-w-6xl mx-auto space-y-8">
            <ProjectManagerModal
              isOpen={isProfileManagerOpen}
              onClose={() => setIsProfileManagerOpen(false)}
              profiles={profiles}
              setProfiles={setProfiles}
              editingProject={editingProject}
              setEditingProject={setEditingProject}
            />

            {currentGroup ? (
              <div className="space-y-8 animate-in fade-in duration-500">
                <CreationHeader
                  currentGroup={currentGroup}
                  setCurrentGroup={setCurrentGroup}
                  setGroups={setGroups}
                  isSegmentsDirty={isSegmentsDirty}
                  isGenerating={isGenerating}
                  setIsGenerating={setIsGenerating}
                  setIsConfirmModalOpen={setIsConfirmModalOpen}
                />

                {(() => {
                  // Determine if the current group's project uses an auto-mode plugin
                  const groupProfile = profiles.find(p => p.id === currentGroup.projectId);
                  // 统一使用 VERTEX_AI 插件
                  const activePlugin = plugins.find(p => p.id === 'VERTEX_AI');
                  const isAutoMode = activePlugin?.workflow === 'auto';

                  if (isAutoMode && groupProfile && activePlugin) {
                    return (
                      <AutoWorkflowPanel
                        group={currentGroup}
                        plugin={activePlugin}
                        project={groupProfile}
                        isGenerating={isGenerating}
                        onPipelineStarted={() => setIsGenerating(true)}
                      />
                    );
                  }

                  // Manual mode — existing UI
                  return (
                    <>
                      <GenerationSettingsPanel
                        group={currentGroup}
                        setCurrentGroup={setCurrentGroup}
                        setGroups={setGroups}
                        setSegments={setSegments}
                      />

                      <TimelineList
                        segments={segments}
                        setSegments={setSegments}
                        currentGroup={currentGroup}
                        setIsSegmentsDirty={setIsSegmentsDirty}
                        modaler={modaler}
                        openPreview={openPreview}
                        uploadingSegmentId={uploadingSegmentId}
                        setUploadingSegmentId={setUploadingSegmentId}
                      />
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-48 text-center bg-zinc-900/10 rounded-none border border-dashed border-zinc-800/50">
                <FolderOpen size={48} className="text-zinc-800 mb-6" />
                <h3 className="text-xl font-bold text-zinc-400">选择或创建一个创作组</h3>
                <p className="text-sm text-zinc-600 mt-2 max-w-xs">从左侧列表选择项目开始您的视觉导演之旅。</p>
              </div>
            )}
          </div>
        </div>

        <VideoPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          playlist={previewPlaylist}
          initialIndex={previewInitialIndex}
        />
      </main>
    </div>
  );
}